import { faCaretDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Button, Divider, Grid, lighten, Typography, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import AddAlertLineReport from "./AddAlertLineReport/AddAlertLineReport";
import AlertLineLanding from "./AlertLineLanding";
import AlertLinePageHeader from "./AlertLinePageHeader";
import PreviousAlertLineReport from "./PreviousAlertLineReport/PreviousAlertLineReport";
import { AlertLinePageContainer } from "./styles";
import { DisclaimerContainer } from "../../../containers/_Layout/styles";
import ConfirmationChangesDialog from "../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import LoadingOverlay from "../../../containers/LoadingOverlay/LoadingOverlay";
import { AlertLineReportDTO } from "../../../models/alert-line-report/alert-line-report-dto";
import { AlertLineURLDTO } from "../../../models/alert-line-url/alert-line-url-dto";
import { AlertLineURLFilter } from "../../../models/alert-line-url/alert-line-url-filter";
import { AlertLineURLLanguageVersionRef } from "../../../models/alert-line-url/alert-line-url-language-version-ref";
import { AvailableLanguageRef } from "../../../models/available-language";
import SimplePopover from "../../../reusable-components/SimplePopover/SimplePopover";
import { PublicAlertLineURLApiService } from "../../../services/public-alert-line-url/public-alert-line-url-api.service";
interface State {
  loading: boolean;
  alertLineURL: AlertLineURLDTO | null;
  error: string;
  invalidURLWarning: boolean;
  currentView: ALERT_LINE_PAGE;
  previousReport: AlertLineReportDTO | null;
  CCDOpen: boolean;
  selectedLanguage: AlertLineURLLanguageVersionRef | null;
}

export enum ALERT_LINE_PAGE {
  LandingPage = "LP",
  NewReport = "NR",
  PreviousReport = "PR",
}

const AlertLinePage = () => {
  const tRP = "pages.alert_line_page";
  const url = window.location.href;
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  const [state, setState] = useState<State>({
    loading: false,
    alertLineURL: null,
    error: "",
    invalidURLWarning: false,
    currentView: ALERT_LINE_PAGE.LandingPage,
    previousReport: null,
    CCDOpen: false,
    selectedLanguage: null,
  });

  //#region load url data
  const loadAlertLineURLData = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const response = await new PublicAlertLineURLApiService().loadItems({
        url,
      } as AlertLineURLFilter);
      if (!response.error) {
        if (response.items.length > 0) {
          const defaultLanguageVersion =
            response.items[0].alertLineURLLanguageVersions.find((languageVersion) => languageVersion.isDefault) ??
            response.items[0].alertLineURLLanguageVersions[0];

          setState((state) => ({
            ...state,
            alertLineURL: response.items[0],
            invalidURLWarning: false,
            selectedLanguage: defaultLanguageVersion ?? null,
          }));

          i18n.changeLanguage(defaultLanguageVersion?.language?.code as string);
        } else {
          setState((state) => ({
            ...state,
            invalidURLWarning: true,
          }));
        }
      } else {
        setState((state) => ({ ...state, error: response.error, alertLineURL: null }));
      }
    } catch (error) {
      setState((state) => ({ ...state, error: error as string }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [url]);
  //#endregion

  useEffect(() => {
    loadAlertLineURLData();
  }, [url]);

  //#region change view
  const handleChangeView = useCallback(
    (view: ALERT_LINE_PAGE, previousReport?: AlertLineReportDTO, requiresConfirmation?: boolean) => {
      if (state.currentView === ALERT_LINE_PAGE.NewReport) {
        if (!requiresConfirmation) {
          setState((state) => ({
            ...state,
            currentView: view,
          }));
        } else {
          setState((state) => ({ ...state, CCDOpen: true }));
        }
      } else {
        setState((state) => ({
          ...state,
          currentView: view,
          previousReport: previousReport ?? null,
        }));
      }
    },
    [state.currentView]
  );
  //#endregion

  //#region set language
  const handleSetLanguage = useCallback(
    (languageVersion: AlertLineURLLanguageVersionRef) => {
      setState((state) => ({
        ...state,
        selectedLanguage: languageVersion,
      }));
      i18n.changeLanguage(languageVersion.language?.code);
    },
    [i18n]
  );
  //#endregion

  //#region child sets language
  const handleSetLanguageByChild = useCallback(
    (language: AvailableLanguageRef) => {
      const languageVersion = state.alertLineURL?.alertLineURLLanguageVersions.find(
        (languageVersion) => languageVersion.language?.id === language.id
      );

      setState((state) => ({
        ...state,
        selectedLanguage: languageVersion ?? null,
      }));
      i18n.changeLanguage(language.code);
    },
    [i18n, state.alertLineURL?.alertLineURLLanguageVersions]
  );
  //#endregion

  return (
    <>
      <AlertLinePageContainer>
        <LoadingOverlay loading={state.loading}>
          {state.error && <Alert severity="error">{state.error}</Alert>}
          {state.alertLineURL ? (
            <>
              <AlertLinePageHeader
                {...(state.currentView !== ALERT_LINE_PAGE.LandingPage && { handleChangeView })}
                {...(state.alertLineURL && {
                  alertLineURLId: state.alertLineURL.id,
                })}
                {...(state.previousReport && {
                  previousReport: state.previousReport,
                  setLoading: (loading: boolean) => setState((state) => ({ ...state, loading })),
                })}
              />
              <Grid style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography>{`${t(`${tRP}.selected_language`)}`}</Typography>
                <SimplePopover
                  buttonType="text"
                  buttonContent={
                    <Grid
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        border: `1px solid ${lighten(theme.palette.secondary.light, 0.5)}`,
                        borderRadius: "3px",
                        padding: "10px",
                      }}
                    >
                      <Typography style={{ textTransform: "none" }}>
                        {state.selectedLanguage?.language?.name}
                      </Typography>
                      <FontAwesomeIcon icon={faCaretDown} />
                    </Grid>
                  }
                >
                  <Grid style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {state.alertLineURL.alertLineURLLanguageVersions.map((languaVersion) => (
                      <React.Fragment key={languaVersion.id}>
                        <Button
                          onClick={() => handleSetLanguage(languaVersion)}
                          style={{
                            fontWeight: languaVersion.language?.id === state.selectedLanguage?.language?.id ? 900 : 400,
                          }}
                        >
                          {languaVersion.language?.name}
                        </Button>
                      </React.Fragment>
                    ))}
                  </Grid>
                </SimplePopover>
              </Grid>
              {state.currentView === ALERT_LINE_PAGE.NewReport ? (
                <AddAlertLineReport
                  alertLineURL={state.alertLineURL}
                  handleChangeView={handleChangeView}
                  selectedLanguage={state.selectedLanguage ?? undefined}
                />
              ) : state.currentView === ALERT_LINE_PAGE.PreviousReport ? (
                <PreviousAlertLineReport
                  alertLineURL={state.alertLineURL}
                  updateParentWithPreviousreport={(report: AlertLineReportDTO) =>
                    setState((state) => ({ ...state, previousReport: report }))
                  }
                  handleSetLanguage={handleSetLanguageByChild}
                />
              ) : state.selectedLanguage ? (
                <AlertLineLanding
                  alertLineURL={state.alertLineURL}
                  handleChangeView={handleChangeView}
                  selectedLanguage={state.selectedLanguage}
                />
              ) : (
                <Typography>{`${t(`${tRP}.no_selected_language`)}`}</Typography>
              )}
            </>
          ) : (
            !state.loading &&
            state.invalidURLWarning && (
              <Grid
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Alert severity="warning">
                  <Typography variant="h5"> This alert line URL is not valid </Typography>
                </Alert>
              </Grid>
            )
          )}
        </LoadingOverlay>
      </AlertLinePageContainer>
      <DisclaimerContainer>
        <Typography variant={"body2"}>
          Powered by{" "}
          <a
            target="_blank"
            rel="noreferrer"
            href="https://www.sommetglobal.com/"
            title={"visit www.sommetglobal.com"}
          >
            Sommet Global
          </a>
        </Typography>
        <Divider orientation="vertical" />
        <Link
          to={"/data-privacy"}
          style={{
            fontSize: theme.typography.body2.fontSize,
            fontWeight: "400",
          }}
        >{`${t("pages.data_privacy_policy.link_title")}`}</Link>
      </DisclaimerContainer>
      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState((state) => ({ ...state, CCDOpen: false }))}
        handleDiscard={() =>
          setState((state) => ({
            ...state,
            currentView: ALERT_LINE_PAGE.LandingPage,
            CCDOpen: false,
          }))
        }
      />
    </>
  );
};
export default AlertLinePage;
